import { authTokenManager } from "@legal-500/auth-client";
import { serviceUnavailableMessage } from "../constants/serviceUnavailableMessages";
import {
  currentServiceAvailabilityGeneration,
  markServiceAvailable,
  markServiceRetrying,
  markServiceUnavailable,
} from "./serviceAvailability";
import {
  MAX_SERVICE_UNAVAILABLE_RETRIES,
  SERVICE_UNAVAILABLE_ERROR,
  isServiceUnavailableBody,
  readServiceUnavailable,
  waitForRetry,
} from "./serviceUnavailable";
import { publicRuntimeConfig } from "../config/publicRuntimeConfig";

const apiBaseUrl = publicRuntimeConfig.apiBaseUrl;

export function url(endpoint: string): string {
  return `${apiBaseUrl}${endpoint}`;
}

function getAuthHeaders(): Record<string, string> {
  const l500Token = authTokenManager.getToken();

  if (l500Token) {
    return {
      "X-L500-Token": l500Token,
    };
  }

  return {};
}

async function fetchRetryingUnavailableGets(url: string, init: RequestInit): Promise<Response> {
  const isGet = (init.method ?? "GET").toUpperCase() === "GET";
  const request = { key: url, generation: currentServiceAvailabilityGeneration() };
  let response = await globalThis.fetch(url, init);
  let unavailable = await readServiceUnavailable(response);

  try {
    for (let retry = 0; unavailable && isGet && retry < MAX_SERVICE_UNAVAILABLE_RETRIES; retry++) {
      markServiceRetrying(request, unavailable.service);
      await waitForRetry(unavailable.retryAfterMs, init.signal);
      response = await globalThis.fetch(url, init);
      unavailable = await readServiceUnavailable(response);
    }
  } catch (error) {
    markServiceAvailable(request);
    throw error;
  }

  if (unavailable && isGet) {
    markServiceUnavailable(request, unavailable.service);
  } else if (!unavailable) {
    markServiceAvailable(request);
  }
  return response;
}

export type ApiError = Error & {
  code?: string;
  status?: number;
  endpoint?: string;
  details?: Array<{ code: string; path: (string | number)[]; message: string }>;
  detailsRaw?: unknown;
  requestId?: string;
  service?: string;
};

async function requestWithUrl<T>(url: string, options?: RequestInit): Promise<T> {
  const authHeaders = getAuthHeaders();
  const headers = { ...authHeaders, ...options?.headers };

  const response = await fetchRetryingUnavailableGets(url, { ...options, headers });

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    let errorCode: string | undefined;
    let errorDetails:
      | Array<{ code: string; path: (string | number)[]; message: string }>
      | undefined;
    let errorDetailsRaw: unknown;
    let requestId: string | undefined;
    let service: string | undefined;

    try {
      const errorData = await response.json();
      if (errorData.error && typeof errorData.error === "string") {
        errorMessage = errorData.error;
      } else if (errorData.error && typeof errorData.error === "object") {
        if (typeof errorData.error.message === "string") {
          errorMessage = errorData.error.message;
        }
        if (typeof errorData.error.code === "string") {
          errorCode = errorData.error.code;
        }
      }
      if (errorData.code && typeof errorData.code === "string") {
        errorCode = errorData.code;
      }
      if (errorData.details !== undefined) {
        errorDetailsRaw = errorData.details;
        if (Array.isArray(errorData.details)) {
          errorDetails = errorData.details;
        }
      }
      if (typeof errorData.request_id === "string") {
        requestId = errorData.request_id;
      } else if (typeof errorData.requestId === "string") {
        requestId = errorData.requestId;
      }
      if (response.status === 503 && isServiceUnavailableBody(errorData)) {
        service = typeof errorData.service === "string" ? errorData.service : undefined;
        errorMessage = serviceUnavailableMessage(service);
        errorCode = SERVICE_UNAVAILABLE_ERROR;
      }
    } catch {
      // If JSON parsing fails, use default error message
    }

    const error = new Error(errorMessage) as ApiError;
    if (errorCode) {
      error.code = errorCode;
    }
    error.status = response.status;
    error.endpoint = url;
    error.details = errorDetails;
    error.detailsRaw = errorDetailsRaw;
    if (requestId) {
      error.requestId = requestId;
    }
    if (service) {
      error.service = service;
    }
    throw error;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

async function request<T>(endpoint: string, options?: RequestInit): Promise<T> {
  return requestWithUrl(url(endpoint), options);
}

export async function get<T>(endpoint: string, headers?: Record<string, string>): Promise<T> {
  return request<T>(endpoint, {
    headers: { ...getAuthHeaders(), ...headers },
  });
}

export async function getWithSignal<T>(
  endpoint: string,
  signal: AbortSignal,
  headers?: Record<string, string>,
): Promise<T> {
  return request<T>(endpoint, {
    headers: { ...getAuthHeaders(), ...headers },
    signal,
  });
}

export async function fetchRaw(endpoint: string, options?: RequestInit): Promise<Response> {
  const authHeaders = getAuthHeaders();
  const headers = { ...authHeaders, ...options?.headers };

  return globalThis.fetch(url(endpoint), {
    ...options,
    headers,
  });
}

export async function post<T>(
  endpoint: string,
  data?: unknown,
  headers?: Record<string, string>,
): Promise<T> {
  return request<T>(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
      ...headers,
    },
    body: data ? JSON.stringify(data) : undefined,
  });
}

export async function postWithSignal<T>(
  endpoint: string,
  data: unknown,
  signal: AbortSignal,
  headers?: Record<string, string>,
): Promise<T> {
  return request<T>(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
      ...headers,
    },
    body: data ? JSON.stringify(data) : undefined,
    signal,
  });
}

export async function put<T>(
  endpoint: string,
  data?: unknown,
  headers?: Record<string, string>,
): Promise<T> {
  return request<T>(endpoint, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
      ...headers,
    },
    body: data ? JSON.stringify(data) : undefined,
  });
}

export async function patch<T>(
  endpoint: string,
  data?: unknown,
  headers?: Record<string, string>,
): Promise<T> {
  return request<T>(endpoint, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
      ...headers,
    },
    body: data ? JSON.stringify(data) : undefined,
  });
}

export async function del<T = void>(
  endpoint: string,
  headers?: Record<string, string>,
): Promise<T> {
  return request<T>(endpoint, {
    method: "DELETE",
    headers: { ...getAuthHeaders(), ...headers },
  });
}
